/**
 * returnLedger.ts — how much of a sale is still returnable.
 *
 * A sale could previously be returned over and over. The return screen capped
 * each line at the quantity SOLD and never looked at what had already come
 * back, and the writers enforced nothing at all, so reopening an invoice
 * produced another refund and another stock increase for goods already
 * returned. `referenceInvoiceId` was written on every credit note and read by
 * nobody.
 *
 * ── Why this matches on product, not on line ─────────────────────────────────
 * A credit note's line items carry productId and quantity but not the id of the
 * line they reverse, and historical credit notes cannot grow one retroactively.
 * So the ledger is kept per product: "no more of product X may come back than
 * went out on this invoice" — which is the actual business rule, and is correct
 * in aggregate even when one product appears on several lines.
 *
 * Where a product does appear twice, the remaining quantity is consumed from
 * the earlier line first. Any distribution satisfies the rule; consuming in
 * order keeps it predictable for the cashier reading the screen.
 *
 * ── Except typed lines ───────────────────────────────────────────────────────
 * Every typed (manual) line points at the store's one placeholder product
 * (manualItem.ts), so "per product" lumped them together: returning "Packing"
 * marked "Transport" as returned and left "Packing" refundable a second time.
 * A line with a description is therefore matched on product + description;
 * credit notes copy the description for exactly this. A returned line with no
 * description (credit notes written before that) still pools by product,
 * consumed in line order, as it always did.
 *
 * Pure — see `returnLedgerDataSource.ts` for the WatermelonDB bridge.
 */

export interface SoldLine {
    /** The original invoice's line item id. */
    id: string;
    productId: string;
    quantity: number;
    /** Set on typed lines — what was typed. Null for catalogue products. */
    description?: string | null;
}

export interface ReturnedLine {
    productId: string;
    quantity: number;
    description?: string | null;
}

/** Product + description for a described line; null for an undescribed one. */
function describedKey(productId: string, description: string | null | undefined): string | null {
    const d = (description ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
    return d ? `${productId}\u0000${d}` : null;
}

/**
 * Remaining returnable quantity for each sold line, keyed by line id.
 *
 * Never negative: an over-return already in the data (from before this existed)
 * reads as zero remaining rather than as a credit to draw against.
 */
export function remainingReturnable(
    sold: SoldLine[],
    returned: ReturnedLine[],
): Record<string, number> {
    const described = new Map<string, number>();
    const byProduct = new Map<string, number>();
    for (const r of returned) {
        const k = describedKey(r.productId, r.description);
        if (k) described.set(k, (described.get(k) ?? 0) + r.quantity);
        else byProduct.set(r.productId, (byProduct.get(r.productId) ?? 0) + r.quantity);
    }

    const take = (pool: Map<string, number>, key: string, want: number) => {
        const have = pool.get(key) ?? 0;
        const got = Math.min(have, want);
        pool.set(key, have - got);
        return got;
    };

    const remaining: Record<string, number> = {};
    for (const line of sold) {
        const k = describedKey(line.productId, line.description);
        let consumed = k ? take(described, k, line.quantity) : 0;
        consumed += take(byProduct, line.productId, line.quantity - consumed);
        remaining[line.id] = Math.max(0, line.quantity - consumed);
    }
    return remaining;
}

/** True when nothing on the invoice can still be returned. */
export function isFullyReturned(remaining: Record<string, number>): boolean {
    return Object.values(remaining).every((q) => q <= 0);
}

export interface ReturnRequest {
    lineItemId: string;
    quantityToReturn: number;
}

export interface ReturnValidation {
    ok: boolean;
    /** Cashier-facing reason, present only when `ok` is false. */
    error?: string;
}

/**
 * Check a requested return against the ledger.
 *
 * The writers call this too, not just the screen: a screen that was opened
 * before another device's return synced is showing a stale cap, and the refund
 * is the last place to catch it.
 */
export function validateReturn(
    requests: ReturnRequest[],
    remaining: Record<string, number>,
    nameForLine: (lineItemId: string) => string = () => 'this item',
): ReturnValidation {
    const wanted = new Map<string, number>();
    for (const r of requests) {
        if (r.quantityToReturn <= 0) continue;
        wanted.set(r.lineItemId, (wanted.get(r.lineItemId) ?? 0) + r.quantityToReturn);
    }

    if (wanted.size === 0) {
        return { ok: false, error: 'Select at least one item to return.' };
    }

    for (const [lineItemId, qty] of wanted) {
        const left = remaining[lineItemId];
        if (left === undefined) {
            return { ok: false, error: 'That line is not part of this invoice.' };
        }
        if (qty > left) {
            return {
                ok: false,
                error: left === 0
                    ? `${nameForLine(lineItemId)} has already been returned in full.`
                    : `Only ${left} of ${nameForLine(lineItemId)} can still be returned.`,
            };
        }
    }
    return { ok: true };
}
